import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/ManageUsers.css';
import api from '../utils/axiosConfig';

function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  const ROLE_TRANSLATIONS = {
    teacher: 'Lehrkraft',
    parent: 'Elternteil',
    admin: 'Administrator',
  };

  useEffect(() => {
  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      const data =  response.data;
    
      // Ensure all users have required fields
      const sanitizedUsers = data.map(user => ({
        _id: user._id || '',
        name: user.name || '',
        code: user.code || '',
        email: user.email || '',
        role: user.role || '',
        institute: user.institute || '',
        active: user.active || false
      }));
      
      setUsers(sanitizedUsers);
      setFilteredUsers(sanitizedUsers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  fetchUsers();
}, []);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const handleDelete = async (userId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?')) return;
    
    try {
      await api.delete(`/api/users/${userId}`);
      setUsers(users.filter(user => user._id !== userId));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleStatusToggle = async (userId) => {
    try {
        const { data } = await api.patch(`/api/users/${userId}/status`);
        setUsers(users.map(user => 
          user._id === userId ? { ...user, active: data.active } : user
        ));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to update status');
      }
    };

  if (loading) return <div className="loading">Loading users...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="manage-container">
      <div className="manage-header">
        <h1>Benutzer</h1>
        <div className="search-container">
          <input
            type="text"
            placeholder="Nach Name oder Code suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-results-count">
            {filteredUsers.length} von {users.length} Benutzern
          </span>
        </div>
      </div>
      <div className="manage-table-wrapper">
      <table className="manage-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>E-Mail</th>
            <th>Rolle</th>
            <th>Institut</th>
            <th>Status</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length > 0 ? (
            filteredUsers.map(user => (
              <tr key={user._id}>
                <td>{user.code}</td>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{ROLE_TRANSLATIONS[user.role] || user.role}</td>
                <td>{user.institute}</td>
                <td>
                  <span className={`status ${user.active ? 'active' : 'inactive'}`}>
                    {user.active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td className="actions">
                  <button 
                    onClick={() => handleStatusToggle(user._id)}
                    className={`status-button ${user.active ? 'deactivate' : 'activate'}`}
                  >
                    {user.active ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                  <button 
                    onClick={() => navigate(`/dashboard/users/edit/${user._id}`)}
                    className="edit-button"
                  >
                    Bearbeiten
                  </button>
                  <button 
                    onClick={() => handleDelete(user._id)}
                    className="delete-button"
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="no-results">
                Keine Benutzer gefunden
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default ManageUsers;